import van, { State as VState, PropsWithKnownKeys } from "vanjs-core";

// van is a reactive UI framework without JSX, but we have fun here.
function h(type: any, props: any, ...children: any) {
  return typeof type === "function"
    ? type(props, ...children)
    : van.tags[type](props, ...children);
}

// For convenience, export van's methods.
h.state = van.state;
h.derive = van.derive;
h.add = van.add;

const queryState = h.state(window.location.search.toString());
const pathnameState = h.state(window.location.pathname);

window.addEventListener("popstate", (_event) => {
  queryState.val = window.location.search;
  pathnameState.val = window.location.pathname;
});

h.localStorageState = (key: string, initialVal: string) => {
  const state = h.state(localStorage[key] ?? initialVal);
  h.derive(() => {
    localStorage[key] = state.val;
  });
  return state;
};

let queuedStateChanges: Array<[string, string]> = [];
function queueStateChange(key: string, val: string) {
  queuedStateChanges.push([key, val]);
  window.setTimeout(() => {
    let query = new URLSearchParams(window.location.search);
    let pathname = window.location.pathname;
    if (queuedStateChanges.length > 0) {
      for (const [key, val] of queuedStateChanges) {
        if (key === "pathname") {
          pathname = val;
          query = new URLSearchParams();
        } else {
          query.set(key, val);
        }
      }
      window.history.pushState(
        undefined,
        "",
        `${pathname}${query.size > 0 ? `?${query}` : ""}`,
      );
      queuedStateChanges = [];
      queryState.val = query.toString();
      pathnameState.val = pathname;
    }
  }, 0);
}

h.pathnameState = () => {
  const state = h.state(window.location.pathname);
  h.derive(() => {
    if (pathnameState.val !== pathnameState.oldVal) {
      state.val = pathnameState.val;
    }
  });
  h.derive(() => {
    if (window.location.pathname !== state.val) {
      queueStateChange("pathname", state.val);
    }
  });

  return state;
};

h.queryParameterState = (key: string, initialVal: string) => {
  const query = new URLSearchParams(window.location.search);
  const state = h.state(query.get(key) ?? initialVal);
  h.derive(() => {
    const newQuery = new URLSearchParams(queryState.val).get(key);
    const oldQuery = new URLSearchParams(queryState.oldVal).get(key);
    if (newQuery !== oldQuery) {
      state.val = newQuery ?? initialVal;
    }
  });

  h.derive(() => {
    if (
      (new URLSearchParams(window.location.search).get(key) ?? initialVal) !==
      state.val
    ) {
      queueStateChange(key, state.val);
    }
  });
  return state;
};

namespace h {
  export type State<T> = VState<T>;
}

export default h;

declare global {
  namespace JSX {
    type IntrinsicElements = {
      [K in keyof HTMLElementTagNameMap]: PropsWithKnownKeys<
        HTMLElementTagNameMap[K]
      >;
    };
  }
}
